/**
 * This class defines CMS-editable action objects as Netx.Models.  The current client-side action object is not yet a Netx.Model, but
 * it's expexted that either we will refactor that to create a proper Netx.Models.Action model, or else we will define how to
 * cross-convert between this model and that object.
 * in CMS v1.0, we believe this model will only be used to create navigation elements.
 * @class
 */
import Base from '@netx/core/lib/netx-model';

import {links as cmsLinks} from '../properties/cms';
import {RegisterLegacyAction} from '@netx/core/legacyActionManager';

export default class CMSAction extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/**
	 * The unique ID for this model
	 * @type {string}
	 */
	get idAttribute() {
		return 'event';
	}

	/**
	 * @property {object}  defaults              - The default attribute values
	 * @property {string}  defaults.event        - non-mutable DAM-unique key, a.k.a. name of the event triggered when action is clicked. Format is cms:(portalId):(uniqueID)
	 * @property {string}  defaults.portalId     - portal for this action
	 * @property {number}  defaults.userId       - owner/creator of this action
	 * @property {string}  defaults.modDate      - auto-set by server on successful update
	 * @property {string}  defaults.label        - human-readable string displayed in the menu; also used by admin UI as human-readable ID.  possibly processed by i18next
	 * @property {number}  defaults.userLevel    - Minimum user level to enable this action
	 * @property {string}  defaults.link         - an URL that a menu action links to.  This can be either absolute (a complete URL) or relative (an internal path) or just a route-fragment (beginning with '#').
	 * @property {string}  defaults.contentLabel - for type asset, need label and asset name
	 * @property {string}  defaults.link       	 - an URL that a menu action links to.  This can be either absolute (a complete URL) or relative (an internal path) or just a route-fragment (beginning with '#').
	 */
	get defaults() {
		return {
			// event: undefined,
			portalId: 0,
			userId: 0,
			modDate: '',
			label: '',
			userLevel: 0,
			link: '',
			contentLabel: '',
			type: '',
		};
	}

	/**
	 * Array of model attributes that are client specific and should never be sent to the server
	 * @type {Array}
	 */
	// clientAttrs: ['userId'], // TODO: it will be safe to send userId again after DAM-6795 is fixed ... not sure when it'll be necessary.
	/**
	 * Array of attributes that are stored on the model in the "goo" property as stringified JSON
	 * @type {Array}
	 */
	get gooAttrs() {
		return ['label', 'userLevel', 'link', 'contentLabel', 'type'];
	}

	/**
	 * RPC methods:
	 * @rpcs
	 */
	get methods() {
		return {
			/** @method create */
			create: [
				'setAction',
				function () {
					return this.self();
				},
			],
			/** @method fetch */
			read: ['getActionForPortal', 'event', 'portalId'],
			/** @method save */
			update: [
				'setAction',
				function () {
					return this.self();
				},
			],
			/** @method destroy */
			remove: ['deleteActionForPortal', 'event', 'portalId'],
		};
	}

	/** @member */
	parse(result, opts) {
		if (_.isObject(result)) {
			this.set(this.unpackAttrs(result), opts);
			return this.attributes;
		} else if (_.isNumeric(result)) {
			// This is the server-side unique id.  We don't use it client-side, but it doesn't hurt.
			this.set({id: result});
		}
	}

	/**
	 * Parses link attribute to get the type of link and it's id(asset, category, page, or just the link itself if it's a url/route)
	 * @param {object} opts opts.edit when true means we want to return values instead of labels
	 * @return {object} returns the type and contentId to be made available to the template
	 */
	parseLink(opts) {
		opts = opts || {};
		var link = this.get('link'),
			linkType,
			contentId;

		// Parse link attribute and sets the type and contentId for use in the template
		if (this.get('type') === 'action') {
			linkType = 'action';
			contentId = opts.edit ? this.get('event') : this.get('event');
		} else if (this.get('type') === 'url' || _.isURL(link)) {
			// see isURL() in core/js/netx.js -- actually quite complicated.
			linkType = 'url';
			contentId = link;
		} else if (~link.indexOf('#asset')) {
			linkType = 'asset';
			contentId = opts.edit ? link.match(/#asset\/(.*)/)[1] : this.get('link');
		} else if (~link.indexOf('#category')) {
			linkType = 'category';
			contentId = opts.edit
				? link.match(/#category\/(.*)/)[1]
				: this.get('link');
		} else if (~link.indexOf('#page')) {
			linkType = 'page';
			contentId = opts.edit ? link.match(/#page\/(.*)/)[1] : this.get('link');
		} else {
			linkType = 'page';
			contentId = link;
		}

		// so not to break current links
		if (!this.get('type')) {
			this.set({type: linkType});
		}

		// Returns object with type and contentId to be made available to the template
		return {
			type: linkType,
			contentId: contentId,
		};
	}

	/**
	 * Register this action in the global actions list.
	 */
	register() {
		RegisterLegacyAction(this.attributes, {silent: true});
	}

	/**
	 * Test whether a link action duplicates any other link action in this.collection.
	 * (If this.collection is undefined, return true.)
	 * @param {string} link the link to test
	 */
	isUniqueLink(link) {
		if (!this.collection) {
			return true;
		}

		// If no link provided, use this model's property.
		link = link || this.get('link');

		return !this.collection.find(function (m) {
			return m.id != this.id && m.get('link') == link.trim();
		}, this);
	}

	// instance version of class method MakeLink:
	makeLink() {
		return this.constructor.MakeLink.apply(this, arguments);
	}

	/**
	 * MakeLink: opposite of parseLink: given type + (id or URL), return a browser-compatible link.
	 * @method
	 * @static
	 * @param {string} type type of link (from cms/properties/cms#links.types)
	 * @param {string} contentId object ID, or raw URL for links of type 'url'
	 */
	static MakeLink(type, contentId) {
		const linkTypes = cmsLinks.types;
		switch (type) {
			case linkTypes.action:
				return contentId;
			case linkTypes.asset:
				return '#asset/' + contentId;
			case linkTypes.category:
				return '#category/' + contentId;
			case linkTypes.url:
				return contentId;
			case linkTypes.page:
				return '#page/' + contentId;
			default:
				throw new Error(
					'Link type "' + type + '" did not match possible types',
				);
		}
	}

	/** @type {string} */
	static get __name__() {
		return 'CMSAction';
	}
}
